// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import './FilterControls.scss';
import { firestore } from '../../context/FirebaseConfig';
import { DateRangePicker, defaultStaticRanges } from 'react-date-range';
import { isSameDay, endOfDay, format, startOfYear, subMonths, startOfDay } from 'date-fns';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Slider from '@mui/material/Slider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';

// Import required styles for the date picker
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { getClaimSettings } from '../../shared_components/utils/FirebaseUtils';

function FilterControls({ onFilterChange, maxDeliveries = 100, maxTonnage = 1000, maxBalesInProduction = 1000 }) {
  const { labelsets, currentUser } = useContext(AuthContext);
  const { t } = useTranslation();
  const [claimIssues, setClaimIssues] = useState([]);
  const datePickerRef = useRef(null);

  const DEFAULT_FILTERS = {
    material: 'all',
    dateRange: {
      selection: {
        startDate: startOfDay(subMonths(new Date(), 6)),
        endDate: endOfDay(new Date()),
        key: 'selection'
      }
    },
    displayedDateRange: t('last_six_months'),
    deliveryRange: [1, null],
    tonnageRange: [null, null],
    balesProductionRange: [null, null],
    selectedClaimIssues: (claimIssues || []).map(issue => issue.key)
  }
  
  const [material, setMaterial] = useState(DEFAULT_FILTERS.material);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState(DEFAULT_FILTERS.dateRange);
  const [deliveryRange, setDeliveryRange] = useState(DEFAULT_FILTERS.deliveryRange);
  const [tonnageRange, setTonnageRange] = useState(DEFAULT_FILTERS.tonnageRange);
  const [balesProductionRange, setBalesProductionRange] = useState(DEFAULT_FILTERS.balesProductionRange);
  const [displayedDateRange, setDisplayedDateRange] = useState(DEFAULT_FILTERS.displayedDateRange);
  const [selectedClaimIssues, setSelectedClaimIssues] = useState(DEFAULT_FILTERS.selectedClaimIssues);

  function resetFilters() {
    setMaterial(DEFAULT_FILTERS.material);
    setDateRange(DEFAULT_FILTERS.dateRange);
    setDeliveryRange(DEFAULT_FILTERS.deliveryRange);
    setDisplayedDateRange(DEFAULT_FILTERS.displayedDateRange);
    setTonnageRange(DEFAULT_FILTERS.tonnageRange);
    setBalesProductionRange(DEFAULT_FILTERS.balesProductionRange);
    setSelectedClaimIssues(DEFAULT_FILTERS.selectedClaimIssues);
  }

  // Notify parent of filter changes
  useEffect(() => {
    notifyFilterChange({
      material: material,
      dateRange: dateRange.selection,
      deliveryRange: { min: deliveryRange[0], max: deliveryRange[1] },
      tonnageRange: { min: tonnageRange[0], max: tonnageRange[1] },
      balesProductionRange: { min: balesProductionRange[0], max: balesProductionRange[1] },
      selectedClaimIssues: selectedClaimIssues.length === claimIssues.length ? [] : selectedClaimIssues // Having all selected claim issues means no filter
    });
  }, [material, dateRange, deliveryRange, tonnageRange, balesProductionRange, selectedClaimIssues]);

  // Get claim issues from firestore
  useEffect(() => {
    getClaimSettings(firestore, currentUser.company.company).then(settings => {
      const newClaimIssues = Object.entries(settings?.delivery_issues || {}).map(([issueKey, issue]) => ({
        key: issueKey,
        display_name: issue.display_name,
      }));
      newClaimIssues.push({
        key: 'claim_without_issues',
        display_name: t('claim_without_issues')
      });
      setClaimIssues(newClaimIssues);

      // Set all claim issues as selected
      setSelectedClaimIssues([...newClaimIssues.map(issue => issue.key)]);
    });
  }, []);

  // If the max amount of deliveries changes, reset the slider to the new max
  useEffect(() => {
    setDeliveryRange(DEFAULT_FILTERS.deliveryRange);
  }, [maxDeliveries]);

  // If the max amount of tonnage changes, reset the slider to the new max
  useEffect(() => {
    setTonnageRange(DEFAULT_FILTERS.tonnageRange);
  }, [maxTonnage]);

  // If the max amount of bales changes, reset the slider to the new max
  useEffect(() => {
    setBalesProductionRange(DEFAULT_FILTERS.balesProductionRange);
  }, [maxBalesInProduction]);

  // Get materials from labelsets
  const materialOptions = useMemo(() => {
    const options = [];
    
    // Add the "All Materials" option first
    options.push({ value: 'all', label: t('all_materials') });
    
    // Add options from labelsets if available
    if (labelsets?.materials?.labels) {
      const materialEntries = Object.entries(labelsets.materials.labels);
      
      // Sort by display_name
      materialEntries.sort((a, b) => {
        const labelA = a[1].display_name || a[0];
        const labelB = b[1].display_name || b[0];
        return labelA.localeCompare(labelB);
      });
      
      // Add each material as an option
      materialEntries.forEach(([key, materialItem]) => {
        options.push({
          value: key,
          label: materialItem.display_name || key
        });
      });
    }
    
    return options;
  }, [labelsets, t]);

  // Custom static ranges similar to TimeframeFilter
  const customStaticRanges = [
    ...defaultStaticRanges,
    {
      label: t('this_year'),
      range: () => ({
        startDate: startOfDay(startOfYear(new Date())),
        endDate: endOfDay(new Date())
      }),
      isSelected(range) {
        const definedRange = this.range();
        return (
          isSameDay(range.startDate, definedRange.startDate) &&
          isSameDay(range.endDate, definedRange.endDate)
        );
      }
    },
    {
      label: t('last_six_months'),
      range: () => ({
        startDate: startOfDay(subMonths(new Date(), 6)),
        endDate: endOfDay(new Date())
      }),
      isSelected(range) {
        const definedRange = this.range();
        return (
          isSameDay(range.startDate, definedRange.startDate) &&
          isSameDay(range.endDate, definedRange.endDate)
        );
      }
    }
  ];

  // Function to notify parent component of filter changes
  const notifyFilterChange = (updatedFilters) => {
    if (onFilterChange) {
      onFilterChange(updatedFilters);
    }
  };

  function handleMaterialChange(e) {
    const newMaterial = e.target.value;
    setMaterial(newMaterial);
  }

  // Handle claim issues selection change. If none are selected, set all claim issues as selected
  function handleClaimIssuesSelectionChange(event) {
    const newSelectedClaimIssues = event.target.value;
    if (newSelectedClaimIssues.length === 0) {
      setSelectedClaimIssues(DEFAULT_FILTERS.selectedClaimIssues);
    } else {
      setSelectedClaimIssues([...newSelectedClaimIssues]);
    }
  }

  function getClaimIssuesDisplayText(selected) {
    if (selected.length === 0) {
      return '';
    }
    if (selected.length === claimIssues.length) {
      return t('claim_filter_all');
    }
    if (selected.length === 1) {
      return claimIssues.find(issue => issue.key === selected[0])?.display_name || selected[0];
    }
    return `${selected.length} ${t('claim_filter_selected')}`;
  }

  function handleDateChange(item) {
    // Ensure startDate is at start of day and endDate is at end of day
    const adjustedSelection = {
      ...item.selection,
      startDate: item.selection.startDate ? startOfDay(item.selection.startDate) : null,
      endDate: item.selection.endDate ? endOfDay(item.selection.endDate) : null,
    };
    
    setDateRange({ selection: adjustedSelection });
    
    // Create a display format for the selected date range
    const start = adjustedSelection.startDate ? format(adjustedSelection.startDate, 'MMM dd, yyyy') : '';
    const end = adjustedSelection.endDate ? format(adjustedSelection.endDate, 'MMM dd, yyyy') : '';
    
    if (start === end) {
      setDisplayedDateRange(start);
    } else {
      setDisplayedDateRange(`${start} - ${end}`);
    }
  }

  function toggleDatePicker() {
    setShowDatePicker(!showDatePicker);
  }

  // Handle clicks outside of the date picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target) && showDatePicker) {
        setShowDatePicker(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  return (
    <div className="filter-controls">
      <div className="filter-group date-range">
        <label className="filter-label">{t('filter_date_range')}</label>
        <div className="date-picker-container" ref={datePickerRef}>
          <button 
            className="date-range-button"
            onClick={toggleDatePicker}
            type="button"
          >
            {displayedDateRange}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </button>
          
          {showDatePicker && (
            <div className="date-picker-dropdown">
              <DateRangePicker
                onChange={handleDateChange}
                ranges={[dateRange.selection]}
                months={1}
                direction="horizontal"
                staticRanges={customStaticRanges}
                inputRanges={[]}
                moveRangeOnFirstSelection={false}
                retainEndDateOnFirstSelection={true}
                rangeColors={['#3d71b2']}
                showSelectionPreview={true}
                showDateDisplay={true}
              />
            </div>
          )}
        </div>
      </div>

      <div className="filter-group">
        <label className="filter-label">{t('filter_material')}</label>
          <Select
            id="material-filter"
            value={material}
            onChange={handleMaterialChange}
            displayEmpty
            className="filter-select"
          >
            {materialOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
      </div>

      <div className="filter-group">
        <label className="filter-label">{t('filter_categories')}</label>
        <Select
          multiple
          value={selectedClaimIssues}
          onChange={handleClaimIssuesSelectionChange}
          input={<OutlinedInput />}
          renderValue={getClaimIssuesDisplayText}
          className="filter-select"
          displayEmpty
        >
          {claimIssues.map((issue) => (
            <MenuItem key={issue.key} value={issue.key} dense>
              <Checkbox checked={selectedClaimIssues.indexOf(issue.key) > -1} size="small" />
              <ListItemText primary={issue.display_name || issue.key} />
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className="filter-group">
        <label className="filter-label">{t('filter_deliveries_range')}</label>
        <SliderWithInput
          min={1}
          max={maxDeliveries}
          value={deliveryRange}
          setValue={setDeliveryRange}
        />
      </div>

      <div className="filter-group">
        <label className="filter-label">{t('filter_tonnage_range')}</label>
        <SliderWithInput
          min={0}
          max={maxTonnage}
          value={tonnageRange}
          setValue={setTonnageRange}
        />
      </div>

      <div className="filter-group">
        <label className="filter-label">{t('filter_bales_range')}</label>
        <SliderWithInput
          min={0}
          max={maxBalesInProduction}
          value={balesProductionRange}
          setValue={setBalesProductionRange}
        />
      </div>

      <button 
        className="filter-reset" 
        onClick={resetFilters}
        type="button"
      >
        {t('filter_reset')}
      </button>
    </div>
  );
}

export default FilterControls; 

function SliderWithInput({ min, max, value, setValue }) {
  function onChange(event, newValue) {
    /* Handle the slider change */
    const firstValue = newValue[0] === 0 && min === 0 ? null : newValue[0];
    const secondValue = newValue[1] === max ? null : newValue[1];
    setValue([firstValue, secondValue]);
  }

  function handleInputChange(index, inputValue) {
    /* Handle the input field change */
    let intValue = parseInt(inputValue, 10);
    if (isNaN(intValue)) return;

    // Bound the value to the min and max
    if (intValue < min) {
      intValue = min;
    } else if (intValue > max) {
      intValue = max;
    }

    const newRange = [...value];
    newRange[index] = intValue;
    
    // Ensure min <= max
    if (index === 0 && newRange[1] !== null && intValue > newRange[1]) {
      newRange[1] = intValue;
    } else if (index === 1 && newRange[0] !== null && intValue < newRange[0]) {
      newRange[0] = intValue;
    }
    
    // Special case for min value when min is 0
    if (index === 0 && intValue === 0 && min === 0) {
      newRange[0] = null;
    }
    
    // Special case for max value
    if (index === 1 && intValue === max) {
      newRange[1] = null;
    }
    
    setValue(newRange);
  }

  // Format values as integers for display
  const displayValue0 = value[0] !== null ? Math.round(value[0]) : Math.round(min);
  const displayValue1 = value[1] !== null ? Math.round(value[1]) : Math.round(max);

  return (
    <div className="delivery-range-slider-container">
      <Slider
        value={[value[0] ?? min, value[1] ?? max]}
        onChange={onChange}
        valueLabelDisplay="auto"
        valueLabelFormat={value => Math.round(value)}
        min={min}
        max={max}
        disableSwap
        sx={{
          color: '#2563eb',
          width: '12rem',
        }}
      />
      <div className="range-input-container">
        <input
          type="text"
          className="range-input"
          value={displayValue0}
          onChange={(e) => handleInputChange(0, e.target.value)}
        />
        <input
          type="text"
          className="range-input"
          value={displayValue1}
          onChange={(e) => handleInputChange(1, e.target.value)}
        />
      </div>
    </div>
  );
}